import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, shorten } from "@/lib/api";
import { useWallet } from "@/context/WalletContext";
import { useLang } from "@/context/LanguageContext";
import { WalletButton } from "@/components/WalletButton";
import { toast } from "sonner";
import { Pencil, Tag, Send, Hexagon, Wallet, Globe2, Loader2, Check } from "lucide-react";

export default function ExplorerProfile() {
  const { id } = useParams();
  const { account } = useWallet();
  const { t } = useLang();
  const target = id || account;
  const isOwner = !id || (account && id.toLowerCase() === account);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");

  const load = useCallback(() => {
    if (!target) return;
    setLoading(true);
    api.get(`/explorers/${target}`).then((r) => { setData(r.data); setName(r.data.explorer_name || ""); })
      .catch(() => setData(null)).finally(() => setLoading(false));
  }, [target]);

  useEffect(() => { load(); }, [load]);

  const saveName = async () => {
    try {
      await api.put("/explorers/me", { wallet_address: account, explorer_name: name });
      setEditing(false);
      toast.success("Explorer designation updated");
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const listSector = async (s) => {
    const price = prompt(`${s.sector_id} — ${t.profile.listPrompt}`, s.price);
    if (!price) return;
    try {
      await api.post("/marketplace/list", { wallet_address: account, sector_id: s.sector_id, sale_price: parseFloat(price) });
      toast.success("OK");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const transferSector = async (s) => {
    const to = prompt(`${s.sector_id} — ${t.profile.transferPrompt}`);
    if (!to) return;
    try {
      await api.post("/marketplace/transfer", { sector_id: s.sector_id, from_wallet: account, to_wallet: to });
      toast.success("Ownership transferred");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!target) {
    return (
      <div className="relative z-10 pt-40 pb-20 max-w-xl mx-auto px-6 text-center">
        <Wallet className="mx-auto text-portal mb-6" size={40} />
        <h1 className="font-display text-3xl uppercase tracking-tight">{t.profile.connectTitle}</h1>
        <p className="text-cosmic/60 mt-4 mb-8 font-light">{t.profile.connectBody}</p>
        <div className="flex justify-center"><WalletButton /></div>
      </div>
    );
  }

  if (loading || !data) {
    return <div className="relative z-10 pt-40 flex justify-center"><Loader2 className="animate-spin text-portal" /></div>;
  }

  const st = data.stats || {};

  return (
    <div className="relative z-10 pt-28 pb-10 max-w-[1500px] mx-auto px-6 md:px-10" data-testid="explorer-profile">
      {/* Header */}
      <div className="glass border border-cosmic/10 p-8 md:p-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="text-overline mb-3">{data.badge || "Explorer"}</div>
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  data-testid="profile-name-input"
                  value={name} onChange={(e) => setName(e.target.value)}
                  className="bg-deepspace border border-cosmic/30 px-3 py-2 font-display text-2xl text-white focus:border-portal outline-none"
                />
                <button data-testid="profile-name-save" onClick={saveName} className="p-2 border border-portal text-portal hover:bg-portal hover:text-deepspace"><Check size={16} /></button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <h1 className="font-display text-3xl md:text-4xl tracking-wide">{data.explorer_name}</h1>
                {isOwner && account && (
                  <button data-testid="profile-edit-btn" onClick={() => setEditing(true)} className="text-cosmic hover:text-portal"><Pencil size={16} /></button>
                )}
              </div>
            )}
            <div className="font-mono text-sm text-cosmic/60 mt-3">{shorten(data.wallet_address)} · {t.profile.joined} {new Date(data.join_date).toLocaleDateString()}</div>
          </div>
          <div className="font-mono text-xs text-cosmic/50">
            {t.profile.publicUrl} · /profile/{data.public_id}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-cosmic/10 border border-cosmic/10 mt-8">
          <Stat icon={<Hexagon size={14} />} label={t.profile.sectorsOwned} value={st.sectors_owned ?? 0} />
          <Stat icon={<Globe2 size={14} />} label={t.profile.surfaceOwned} value={`${(st.surface_owned_km2 ?? 0).toLocaleString()} km²`} />
          <Stat icon={<Wallet size={14} />} label={t.profile.investment} value={`${st.total_investment_usdc ?? 0} USDC`} />
          <Stat icon={<Tag size={14} />} label={t.profile.discoveries} value={st.discoveries ?? 0} />
        </div>
      </div>

      {/* Owned sectors */}
      <h2 className="font-display text-2xl uppercase tracking-tight mt-14 mb-6">{t.profile.registered}</h2>
      {data.owned_sectors?.length ? (
        <div className="border border-cosmic/10">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-overline border-b border-cosmic/10">
            <div className="col-span-2">{t.profile.sector}</div><div className="col-span-3">{t.profile.designation}</div>
            <div className="col-span-3">{t.profile.coordinates}</div><div className="col-span-2">{t.profile.token}</div>
            <div className="col-span-2 text-right">{t.profile.actions}</div>
          </div>
          {data.owned_sectors.map((s) => (
            <div key={s.sector_id} data-testid={`owned-${s.sector_id}`} className="grid grid-cols-2 md:grid-cols-12 gap-4 px-5 py-4 border-b border-cosmic/10 hover:bg-portal/5 transition-colors items-center text-sm">
              <div className="col-span-2 font-mono text-portal">{s.sector_id}</div>
              <div className="col-span-3 font-grotesk">{s.name}</div>
              <div className="col-span-3 font-mono text-cosmic/70 text-xs">{s.lat}°, {s.lng}°</div>
              <div className="col-span-2 font-mono text-cosmic/70">#{s.nft_token_id} {s.for_sale && <span className="text-mars">· listed</span>}</div>
              {isOwner && account ? (
                <div className="col-span-2 flex justify-end gap-2">
                  <button onClick={() => listSector(s)} title={t.profile.list} className="p-2 border border-cosmic/20 hover:border-portal hover:text-portal"><Tag size={13} /></button>
                  <button onClick={() => transferSector(s)} title={t.profile.transfer} className="p-2 border border-cosmic/20 hover:border-portal hover:text-portal"><Send size={13} /></button>
                </div>
              ) : <div className="col-span-2" />}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-cosmic/50 font-light">{t.profile.noSectors}</p>
      )}

      {/* Transactions */}
      <h2 className="font-display text-2xl uppercase tracking-tight mt-14 mb-6">{t.profile.txHistory}</h2>
      {data.transactions?.length ? (
        <div className="border border-cosmic/10">
          {data.transactions.map((t) => (
            <div key={t.id} className="grid grid-cols-3 md:grid-cols-5 gap-4 px-5 py-3 border-b border-cosmic/10 text-xs font-mono text-cosmic/70 items-center">
              <span className="uppercase text-portal">{t.type}</span>
              <span>{t.sector_id}</span>
              <span className="hidden md:block">{shorten(t.tx_hash)}</span>
              <span>{t.amount} {t.currency}</span>
              <span className="text-right">{new Date(t.timestamp).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      ) : <p className="text-cosmic/50 font-light">{t.profile.noTx}</p>}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-deepspace/40 p-5">
      <div className="flex items-center gap-1.5 text-overline mb-2">{icon}{label}</div>
      <div className="font-display text-xl md:text-2xl text-white">{value}</div>
    </div>
  );
}
